import React, { useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import DataTable from "react-data-table-component";
import { Button, Spinner, Alert, Fade } from "react-bootstrap";
import { format } from "date-fns";

import { ReportSearchForm } from "./ReportSearchForm";
import { actions } from "../redux/reportsSaga";
import { reportReducerActions } from "..";
import { rtSagaActions, rtReducerActions } from "../../ReceiptTracking";

const XLSX = require('xlsx');

const date = Date().split(" ");
const dateStr = date[2] + date[1] + date[3];

const moneyFormater = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "PGK"
});

const presentDate = new Date();
const formatPresentDate = format(presentDate, "dd/MM/yyyy");

// Blatant "inspiration" from https://codepen.io/Jacqueline34/pen/pyVoWr
function convertArrayOfObjectsToCSV(array) {
  let result;

  const columnDelimiter = ",";
  const lineDelimiter = "\n";
  let keys = Object.keys(array[0]);

  keys = keys.map((k) => {
    let key = {};
    key.value = k;
    key.name = k.replaceAll("_", " ");
    key.name = key.name.split(' ').map(str => (
      str.charAt(0).toUpperCase() + str.slice(1)
    )).join(' ');
    
    return key;
  })
  
  result = "";
  result += keys.map(k => k.name).join(columnDelimiter);
  result += lineDelimiter;
  
  array.forEach((item) => {
    
    let ctr = 0;
    keys.forEach((key) => {
      if (ctr > 0) result += columnDelimiter;

      result += item[key.value];

      ctr++;
    });
    result += lineDelimiter;
  });
  
  return result;
};

// Blatant "inspiration" from https://codepen.io/Jacqueline34/pen/pyVoWr
function downloadCSV(array, reportName) {
  if(array.length === 0){
    return
  }
  const link = document.createElement("a");
  let csv = convertArrayOfObjectsToCSV(array);
  if (csv == null) return;

  const filename = `${reportName} Report_${dateStr}.csv`;

  if (!csv.match(/^data:text\/csv/i)) {
    csv = `data:text/csv;charset=utf-8,${csv}`;
  }

  link.setAttribute("href", encodeURI(csv));
  link.setAttribute("download", filename);
  link.click();
};

function downloadEXCEL(array, reportName) {
  if(array && array.length === 0){
    return
  }

  let worksheet = XLSX.utils.json_to_sheet(array);
  var workBook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workBook, worksheet, reportName);

  XLSX.writeFile(workBook, `${reportName} Report_${dateStr}.xlsx`);
  
};

function downloadPDFReport(dataStr, reportName){
  if (dataStr){
    if (dataStr.length === 0){
      alert("PDF file was not generated.");
      return;
    }
    const filename = `${reportName} Report_${dateStr}.pdf`;
    const link = document.createElement("a");
    let data = 'data:application/pdf;base64,' + dataStr;
    link.setAttribute("href", data);
    link.setAttribute("download", filename);
    link.click();
  } else {
    alert("PDF file was not generated.");
    return
  }
};

const sortReportParams = (queryParams) => {
  let parameters = [
    {
      paramName: "p_departmentId",
      paramValue: queryParams.p_departmentId,
      paramDataType: "java.lang.Integer"
    },
    {
      paramName: "p_serviceTypeId",
      paramValue: queryParams.p_serviceTypeId,
      paramDataType: "java.lang.Integer"
    },
    {
      paramName: "p_serviceItemId",
      paramValue: queryParams.p_serviceItemId,
      paramDataType: "java.lang.Integer"
    },
    {
      paramName: "p_provinceId",
      paramValue: queryParams.p_provinceId,
      paramDataType: "java.lang.Integer"
    },
    {
      paramName: "p_fromDate",
      paramValue: queryParams.p_fromDate,
      paramDataType: "java.sql.Date"
    },
    {
      paramName: "p_toDate",
      paramValue: queryParams.p_toDate,
      paramDataType: "java.sql.Date"
    },
    {
      paramName: "p_timeInterval",
      paramValue: queryParams.p_timeInterval,
      paramDataType: "java.lang.String"
    }
  ];

  parameters = parameters.filter((param) => param.paramValue !== "" && param.paramValue !== null && param.paramValue !== undefined );
  
  return parameters;
};

const Export = ({ onCSVExport, onPDFExport, onExcelExport, reportLoading, reportName }) => (
  <>
    <Button variant="primary" onClick={(e) => onCSVExport(e.target.value)} style={{fontSize: "14px"}}>CSV</Button>{" "}
    <Button variant="primary" onClick={(e) => onPDFExport(e.target.value)} style={{fontSize: "14px"}} disabled={reportLoading || !reportName} >PDF</Button>{" "}
    <Button variant="primary" onClick={(e) => onExcelExport(e.target.value)} style={{fontSize: "14px"}}>EXCEL</Button>
  </>
);

const columns = [
  {
    name: "Receipt No.",
    selector: (row) => row.rcpt_number,
    sortable: true,
  },
  {
    name: "Status",
    selector: (row) => row.rcpt_status_desc,
    sortable: true,
  },
  {
    name: "Customer",
    selector: (row) => row.cust_first_name + " " + row.cust_last_name,
    sortable: true,
  },
  {
    name: "Service Type",
    selector: (row) => row.service_name,
    sortable: true,
  },
  {
    name: "Service Item",
    selector: (row) => row.service_desc,
    sortable: true,
  },
  {
    name: "Mobile",
    selector: (row) => row.cust_msisdn,
    sortable: true,
  },
  {
    name: "Department",
    selector: (row) => row.dept_name,
    sortable: true,
  },
  {
    name: "Province",
    selector: (row) => row.province_name,
    sortable: true
  },
  {
    name: "Amount",
    selector: (row) => moneyFormater.format(row.total_amnt),
    sortable: true
  },
  {
    name: "Date Issued",
    selector: (row) => row.rcpt_issued_date,
    sortable: true
  }
];

const dataTableStyles = {
  rows: {
    style: {
      fontSize: "15px"
    },
  },
  headCells: {
    style: {
      fontSize: "16px"
    },
  },
  cells: {
    style: {
      fontSize: "15px"
    },
  },
  pagination: {
    style: {
      fontSize: "16px"
    }
  }
};

export function ReportTable() {

  const dispatch = useDispatch();

  const { reports, receiptTracking } = useSelector(state => state);
 
  const { reportDataString, fetchingReportStr } = reports;
  const { masterReceipts:receiptData, listLoading:rtListLoading, receiptsMaxLimit, receiptMaxErrorMsg } = receiptTracking;

  const [ showRcptLimitErr, setShowRcptLimitErr ] = useState(false);
  
  const [reportName, setReportName] = useState("");
  const [ filteredData, setFilteredData ] = useState([]);
  const [ resetPagination, setResetPagination ] = useState(false);
  
  const actionsMemo = useMemo(() => 
    <Export 
      onPDFExport={() => downloadPDFReport(reportDataString, reportName)} 
      onCSVExport={() => downloadCSV(filteredData, reportName)} 
      onExcelExport={() => downloadEXCEL(filteredData, reportName)}
      reportLoading={fetchingReportStr}
      reportName={reportName} 
    />,
  [filteredData, reportName, reportDataString, fetchingReportStr]);

  useEffect(() => {
    //reset first on initial load
    dispatch(reportReducerActions.reportStateClear());

    dispatch(rtSagaActions.fetchMasterReceipts({ 
      fromIssuedDate: formatPresentDate,
      toIssuedDate: formatPresentDate
    }));
  }, [dispatch]);

  useEffect(() => {
    let filteredRcpt = receiptData;
    
    if (reportName === "Revenue Collections"){

      filteredRcpt = receiptData.filter((rcpt) => ( rcpt.rcpt_status_desc === "Issued" || rcpt.rcpt_status_desc === "Used" ));
      
    } else if (reportName === "Services Rendered"){

      filteredRcpt = receiptData.filter((rcpt) => ( rcpt.rcpt_status_desc === "Used"));
    } else if (reportName === "Transactions"){

      filteredRcpt = receiptData.filter((rcpt) => ( rcpt.rcpt_status_desc === "Issued" || rcpt.rcpt_status_desc === "Used" || rcpt.rcpt_status_desc === "Refund Completed"));
    } else if (reportName === "Receipts Collected"){

      filteredRcpt = receiptData;
    }
    
    setFilteredData(filteredRcpt);
  }, [receiptData, reportName]);

  useEffect(() => {
    if(receiptsMaxLimit){
      setShowRcptLimitErr(true);
      setTimeout(() => {
        dispatch(rtReducerActions.clearReceiptMaxLimit());
        setShowRcptLimitErr(false);
      }, 8000);
    } else {
      setShowRcptLimitErr(false);
    }
  }, [receiptsMaxLimit, dispatch, receiptMaxErrorMsg]);

  const closeAlert = () => {
    dispatch(rtReducerActions.clearReceiptMaxLimit());
    setShowRcptLimitErr(false);
  };

  const handleReportReset = () => {
    setReportName("");
    dispatch(rtSagaActions.fetchMasterReceipts({
      fromIssuedDate: formatPresentDate,
      toIssuedDate: formatPresentDate
    }));
  };

  
  const handleReportRequest = (queryParams) => {
    setReportName(queryParams.p_reportType);
    setResetPagination(true);
    dispatch(reportReducerActions.reportStateClear());
    dispatch(reportReducerActions.reportStringCleared());
    handleGeneratePDFReport(queryParams);

    let newQueryParams = { 
      fromIssuedDate: queryParams.p_fromDate, 
      toIssuedDate: queryParams.p_toDate,
      departmentId: queryParams.p_departmentId, 
      serviceId: queryParams.p_serviceTypeId,
      serviceItemId: queryParams.p_serviceItemId,
      provinceId: queryParams.p_provinceId
    };
    
    dispatch(rtSagaActions.fetchMasterReceipts(newQueryParams));
    setResetPagination(false);
    
  };

  const handleGeneratePDFReport = (queryParams) => {
    let definedParams = sortReportParams(queryParams);

    let reportQueryParams = {
      reportFileNameWithExt: queryParams.p_reportType === "Receipts Collected" ? "receipt-collections-report.jrxml" 
        : ( queryParams.p_reportType === "Revenue Collections" ? "revenue-collections-report.jrxml" 
        : (queryParams.p_reportType === "Services Rendered" ? "services-rendered-report.jrxml" 
        : (queryParams.p_reportType === "Transactions" && "transactions-report.jrxml"))),
      parameters: definedParams,
      generatedFileName: `${queryParams.p_reportType} Report ${dateStr}`,
      reportOutputTypeCode: "PDF",
      saveFile: false
    };
    
    dispatch(actions.findReports(reportQueryParams));
  };

  return (
    <div className="card card-custom card-stretch gutter-b">
      {/* begin::Header */}

      {/* end::Header */}

      {/* begin::Body */}

      {
        showRcptLimitErr ?
        <div className="d-flex justify-content-end">
          <Alert variant="info" transition={Fade} onClose={closeAlert} dismissible style={{ width: "50%", zIndex: 2, position: "absolute" }} >
            <Alert.Heading > Maximum Receipt Records Reached </Alert.Heading>
            <p>
              <b> {receiptMaxErrorMsg} </b>
              <br/>
              Adjust your search parameters to make a narrow or specific search for receipt records.
            </p>
          </Alert>
        </div>
        : null  
      }
      
      <div className="card-body py-6">
        <div className="row justify-content-start">
          <div className="col-12">
            <ReportSearchForm onHandleReportRequest={handleReportRequest} onHandleReset={handleReportReset} />
          </div>
        </div>
        <div className="row pt-10">
          <div className="col-12">
            <DataTable
              title={reportName}
              customStyles={dataTableStyles}
              columns={columns}
              data={filteredData}
              actions={actionsMemo}
              progressPending={rtListLoading}
              progressComponent={<Spinner variant="primary" animation="border" />}
              pagination
              paginationResetDefaultPage={resetPagination}
            />
            {/* end::Table */}
          </div>
        </div>
      </div>
      {/* end::Body */}
    </div>
  );
}
